import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type NavigateFunction, useNavigate } from 'react-router-dom';
import styled, { css, useTheme } from 'styled-components';

import { GeodataModel } from '../../../../../../typings/api/skymap/rest/v1/.common';
import { useDialog } from '../../../hooks/use-dialog';
import { AppContext } from '../../../state/app-state';
import { reset } from '../../../utils/styled-reset';
import { Icon, IconStyled } from '../../icon/icon';
import { Stack } from '../../stack/stack';
import { GcpAdjustmentDialog } from '../gcp-adjustment-dialog';
import { geodataStatuses } from '../use-geodata';
import { DeleteGeodataDialog } from './delete-geodata-dialog';

const buildGcpPagePath = (geodata?: Pick<GeodataModel, 'id' | 'projectId'>) => {
  if (!geodata) {
    return null;
  }

  return `/projects/${geodata.projectId}/geodata/${geodata.id}/viewer`;
};

const goToGcpPage = (
  geodata?: Pick<GeodataModel, 'id' | 'projectId'>,
  navigate?: NavigateFunction,
) => {
  const gcpPagePath = buildGcpPagePath(geodata);

  if (!gcpPagePath) {
    return;
  }

  if (navigate) {
    void navigate(gcpPagePath);
    return;
  }

  window.location.assign(gcpPagePath);
};

interface Props {
  item: GeodataModel;
  selected: boolean;
  translatedProcessType: string;
  onClick: () => void;
  onDeleteDialogClosed: (wasDeleted: boolean) => void;
}

const GeodataStatusIcon = (props: { state: ItemState }) => {
  const theme = useTheme();

  if (props.state === 'success') {
    return <Icon color={theme.color.green} icon={['fad', 'check-circle']} size="sm" />;
  }
  if (props.state === 'failed') {
    return <Icon color={theme.color.red} icon={['fad', 'times-circle']} size="sm" />;
  }
  if (props.state === 'pending') {
    return <Icon color={theme.color.gray.dark} icon={['fad', 'spinner']} size="sm" spin={true} />;
  }
  if (props.state === 'placeMarker') {
    return <Icon color={theme.color.gray.dark} icon={['fad', 'drone']} size="sm" />;
  }

  // Unknown status
  return <Icon color={theme.color.gray.medium} icon={['fad', 'question-circle']} size="sm" />;
};

type ItemState = 'failed' | 'success' | 'pending' | 'placeMarker' | 'unknown';

function getItemState(status: GeodataModel['status']): ItemState {
  if (geodataStatuses.successStatuses.includes(status)) {
    return 'success';
  }
  if (geodataStatuses.failedStatuses.includes(status)) {
    return 'failed';
  }
  if (geodataStatuses.pendingStatuses.includes(status)) {
    return 'pending';
  }
  if (geodataStatuses.placeMarkerStatuses.includes(status)) {
    return 'placeMarker';
  }
  return 'unknown';
}

const GeodataListItem = (props: Props) => {
  const { t } = useTranslation();
  const { isOrganizationAdmin } = useContext(AppContext);
  const navigate = useNavigate();
  const theme = useTheme();
  const deleteGeodataDialog = useDialog();
  const state = useMemo<ItemState>(() => {
    return getItemState(props.item.status);
  }, [props.item.status]);

  const showGcpPageIcon = useMemo<boolean>(() => {
    return isOrganizationAdmin || props.item.processType !== 'ORTHO_ONLY';
  }, [isOrganizationAdmin, props.item.processType]);

  const gcpAdjustmentDialog = useDialog();
  const onCloseGcpAdjustmentDialog = (confirmed: boolean) => {
    gcpAdjustmentDialog.hide();
    if (confirmed) {
      goToGcpPage(props.item, navigate);
    }
  };

  return (
    <Component selected={props.selected} state={state} onClick={props.onClick}>
      <div>
        <Stack alignItems="center" direction="row" spacing={0.5}>
          {/* <GeodataStatusIcon status={props.item.status} /> */}
          <GeodataName className="notranslate">{props.item.name}</GeodataName>
        </Stack>
        <Stack alignItems="center" direction="row" spacing={0.5}>
          <GeodataStatusIcon state={state} />
          <Geodatatype>{props.translatedProcessType}</Geodatatype>
        </Stack>
      </div>

      <ActionIconsContainer>
        <Stack direction="row" spacing={0.5}>
          {showGcpPageIcon && (
            <Icon
              color={theme.color.gray.dark}
              data-testid="settings-icon"
              icon={['fad', 'drone']}
              size="lg"
              title={t('goToGcpPage', { ns: 'cloudProcessing' })}
              onClick={() => {
                if (
                  props.item.gcpCorrectionJobRequest &&
                  !props.item.gcpCorrectionJobRequest.selfReview
                ) {
                  gcpAdjustmentDialog.show();
                } else {
                  void goToGcpPage(props.item, navigate);
                }
              }}
              onHoverStyle={{ icon: ['fas', 'drone'] }}
            />
          )}
          <Icon
            color={theme.color.gray.dark}
            data-testid="delete-icon"
            icon={['fal', 'trash-can']}
            size="lg"
            title={t('delete', { ns: 'common' })}
            onClick={() => deleteGeodataDialog.show()}
            onHoverStyle={{ icon: ['fas', 'trash-can'] }}
          />
        </Stack>
      </ActionIconsContainer>

      {deleteGeodataDialog.render(
        <DeleteGeodataDialog
          item={props.item}
          onClose={(wasDeleted) => {
            deleteGeodataDialog.hide();
            props.onDeleteDialogClosed(wasDeleted);
          }}
        />,
      )}
      {gcpAdjustmentDialog.render(<GcpAdjustmentDialog onClose={onCloseGcpAdjustmentDialog} />)}
    </Component>
  );
};

const GeodataName = styled.span`
  display: block;
  font-weight: 500;
`;
const Geodatatype = styled.span`
  font-weight: 300;
`;

const ActionIconsContainer = styled.div``;

const Component = styled.li<{ selected: boolean; state: ItemState }>`
  ${reset}

  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 0.2em;

  width: 100%;
  font-size: 14px;
  padding: 0.7em;
  cursor: pointer;
  background-color: ${(props) => (props.selected ? props.theme.color.selected : '#f5f5f5')};

  border: 1px solid ${(props) => props.theme.color.gray.light};
  border-left: 4px solid
    ${(props) => {
      // return props.theme.color.brand.dark;
      if (props.state === 'success') {
        return props.theme.color.green;
      }
      if (props.state === 'failed') {
        return props.theme.color.red;
      }
      if (props.state === 'placeMarker') {
        return props.theme.color.brand.dark;
      }
      return props.theme.color.gray.medium;
    }};
  &:not(:first-child) {
    border-top: none;
  }

  ${(props) =>
    !props.selected &&
    css`
      &:not(:hover) {
        ${ActionIconsContainer} ${IconStyled} {
          display: none;
        }
      }

      &:hover {
        background-color: #e5e5e5;
      }
    `}
`;

export { GeodataListItem };
